import clsx from 'clsx';

import { Song, SongPreview } from '~/interfaces';

interface Props {
  width: number;
  height: number;
  songData: SongPreview | Song;
  children: React.ReactNode;
  background?: React.ReactNode;
  /**
   * Turns the stacked title block into a single line and puts this at the end of it.
   *
   * The post-game screens ask for it: stacked, the title and artist are ~150px of a 1080 screen that
   * has four singer panels to fit, and the frames draw the pair inline with the song's difficulty
   * beside them. Left off, the header stays as every other page has it.
   */
  headerRow?: React.ReactNode;
}

export default function SongPage({ songData, background, width, height, children, headerRow, ...restProps }: Props) {
  const author = songData.author && (
    <>
      by&nbsp;
      {songData.authorUrl ? <a href={songData.authorUrl}>{songData.author}</a> : songData.author}
    </>
  );

  return (
    <div className="relative overflow-y-auto bg-black" style={{ width, height }} {...restProps}>
      <BackgroundImage video={songData.video} blur />
      {background && <div className="fixed inset-0">{background}</div>}

      <div className="relative mx-auto flex h-full max-w-440 flex-col px-4 text-white">
        {headerRow ? (
          <div className="mt-2 mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-white">
            <span className="ph-no-capture typography text-active stroke-text text-lg font-bold 2xl:text-[2.125rem]">
              {songData.title}
            </span>
            <span className="ph-no-capture typography stroke-text text-md font-bold text-white/65! 2xl:text-xl">
              {songData.artist}
            </span>
            {author && <span className="typography text-xs text-white/45! 2xl:text-sm">{author}</span>}
            {headerRow}
          </div>
        ) : (
          <div className="pointer-events-none mb-4 flex flex-col items-start gap-1 text-white">
            <ContentElement className="text-active mt-2 text-lg lg:text-xl 2xl:mt-5 2xl:text-5xl">
              {songData.title}
            </ContentElement>
            <ContentElement className="text-md lg:text-lg 2xl:text-4xl">{songData.artist}</ContentElement>
            {author && <ContentElement className="lg:text-md text-sm 2xl:text-xl">{author}</ContentElement>}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

export const ContentElement = ({ className, ...restProps }: React.ComponentProps<'span'>) => (
  <span className={clsx('typography inline-block bg-black/50 px-5 py-0.5', className)} {...restProps} />
);

const BackgroundImage = ({ blur, video }: { blur: boolean; video: string }) => (
  <div
    className={clsx('fixed inset-0 z-0 bg-cover bg-center', blur ? 'blur-sm' : undefined)}
    style={{ backgroundImage: `url('https://i3.ytimg.com/vi/${video}/hqdefault.jpg')` }}
  />
);
